// Collects live telemetry from the multipart engine so the UI can render a
// packet-transfer diagram, throughput-over-time chart and efficiency stats.
//
// The monitor is a plain mutable object (no React state) — the component samples
// snapshot() on a timer while uploads are active, which keeps re-renders cheap.

// Smoothed (EMA) instantaneous speed sampler so per-file "MB/s" reflects
// current conditions, not a sluggish all-time average.
export const makeSpeedSampler = () => {
  let primed = false;
  let lastLoaded = 0;
  let lastTs = 0;
  let ema = 0;
  return (loaded) => {
    const now = Date.now();
    if (!primed) {
      primed = true;
      lastLoaded = loaded;
      lastTs = now;
      return 0;
    }
    const dt = (now - lastTs) / 1000;
    if (dt >= 0.5) {
      const inst = Math.max(0, (loaded - lastLoaded) / dt);
      ema = ema > 0 ? ema * 0.6 + inst * 0.4 : inst;
      lastLoaded = loaded;
      lastTs = now;
    }
    return ema;
  };
};

export function createMonitor() {
  const state = {
    // active file's parts: PartNumber -> { state, loaded, size }
    parts: new Map(),
    activeName: '',
    activePartCount: 0,
    activeFileSize: 0,
    // cumulative across the whole session
    retries: 0,
    retriedBytes: 0,
    usefulBytes: 0, // bytes that counted toward completed parts
    wireBytes: 0, // bytes actually PUT over the network this session
    // throughput ring buffer: { t, bps }
    samples: [],
    peakBps: 0,
  };

  const startFile = ({ name, partCount, fileSize }) => {
    state.parts = new Map();
    state.activeName = name || '';
    state.activePartCount = partCount || 0;
    state.activeFileSize = fileSize || 0;
  };

  const partState = (n, st, loaded, size) => {
    const prev = state.parts.get(n);
    if (st === 'done' && (!prev || prev.state !== 'done')) state.usefulBytes += size || 0;
    state.parts.set(n, { state: st, loaded: loaded || 0, size: size || 0 });
  };

  const addRetry = (wastedBytes) => {
    state.retries += 1;
    state.retriedBytes += Math.max(0, wastedBytes || 0);
  };

  // Bytes that actually crossed the wire (from XHR upload progress). Resumed /
  // already-uploaded parts are NEVER reported here, so throughput stays honest
  // when a guest re-adds a folder to resume.
  const addWire = (delta) => {
    if (delta > 0) state.wireBytes += delta;
  };

  const finishFile = () => {
    // leave parts as-is so the grid shows the finished file briefly
  };

  // Sampled on a steady cadence by the component. Throughput is derived from
  // bytes that genuinely crossed the wire (state.wireBytes), so instantly
  // credited resume/skip bytes can never spike the curve.
  const sampleThroughput = () => {
    const now = Date.now();
    const total = state.wireBytes;
    const last = state.samples[state.samples.length - 1];
    if (last) {
      const dt = (now - last.t) / 1000;
      if (dt >= 0.4) {
        const bps = Math.max(0, (total - last.loaded) / dt);
        state.samples.push({ t: now, loaded: total, bps });
        if (bps > state.peakBps) state.peakBps = bps;
      }
    } else {
      state.samples.push({ t: now, loaded: total, bps: 0 });
    }
    if (state.samples.length > 90) state.samples.splice(0, state.samples.length - 90);
  };

  const reset = () => {
    state.parts = new Map();
    state.samples = [];
    state.retries = 0;
    state.retriedBytes = 0;
    state.usefulBytes = 0;
    state.wireBytes = 0;
    state.peakBps = 0;
  };

  const snapshot = () => {
    const parts = [];
    for (let n = 1; n <= state.activePartCount; n += 1) {
      const p = state.parts.get(n) || { state: 'queued', loaded: 0, size: 0 };
      parts.push({ n, ...p });
    }
    const sent = state.usefulBytes + state.retriedBytes;
    return {
      activeName: state.activeName,
      partCount: state.activePartCount,
      parts,
      doneParts: parts.filter((p) => p.state === 'done').length,
      activeParts: parts.filter((p) => p.state === 'active').length,
      retries: state.retries,
      retriedBytes: state.retriedBytes,
      sentBytes: sent,
      usefulBytes: state.usefulBytes,
      efficiency: sent > 0 ? state.usefulBytes / sent : 1,
      samples: state.samples.slice(),
      peakBps: state.peakBps,
    };
  };

  return { startFile, partState, addRetry, addWire, finishFile, sampleThroughput, reset, snapshot };
}
